import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAnalytics } from '@modernhealth/web-analytics';
import { EventLexicon } from '@modernhealth/analytics-lexicon';

import { toastNotificationsService } from 'src/config/rootService';
import { getFirstName } from 'src/modules/user/selectors/userSelectors';
import { CreatePathwayError } from 'src/modules/pathways/types';

import { usePathwaysNavigation } from '../hooks/navigation';
import { useCreateMemberPathwayMutation } from '../documents/createMemberPathway.generated';
import { PathwayDocument } from '../documents/pathway.generated';
import { CreatePathwayLayout } from '../components/CreatePathwayLayout';

export const PathwayCreateSummaryPage = () => {
    const { navigate } = usePathwaysNavigation();
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const { uuid } = useParams<{ uuid: string }>();
    const firstName = useSelector(getFirstName);
    const [createMemberPathway] = useCreateMemberPathwayMutation({
        refetchQueries: [{ query: PathwayDocument, variables: { uuid } }],
        awaitRefetchQueries: true,
        variables: {
            input: {
                pathwayUuid: uuid,
            },
        },
    });

    return (
        <CreatePathwayLayout
            subtitle={t('pathways:onboarding.summary.subtitle', { name: firstName })}
            title={t('pathways:onboarding.summary.title')}
            description={t('pathways:onboarding.summary.description')}
            buttonText={t('pathways:action.continue')}
            buttonArrow
            onSubmit={async () => {
                analytics.trackEvent(
                    EventLexicon.clickCreatePathway({
                        pathwayUuid: uuid,
                    }),
                );
                try {
                    const { data } = await createMemberPathway();
                    if (data?.createMemberPathway?.ok) {
                        navigate.pathway({ uuid });
                    } else {
                        throw new Error('Unable to create pathway');
                    }
                } catch (error) {
                    if (`${error}`.includes(CreatePathwayError.ALREADY_CREATED)) {
                        toastNotificationsService.renderErrorToast(
                            'pathways:onboarding.create.error.already_created',
                        );
                        navigate.pathway({ uuid });
                    } else {
                        toastNotificationsService.renderErrorToast(
                            'pathways:onboarding.create.error.generic',
                        );
                        throw error;
                    }
                }
            }}
            onClose={() => navigate.pathway({ uuid })}
        />
    );
};
